import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  CRUD_ENTITY_CONFIGS,
  type CrudEntityConfig,
  type CrudEntityKey,
  type CrudRecord,
  type FieldDefinition,
  type FieldOption,
  isCrudEntityKey
} from '../../core/config/entity-config';
import { DataStoreService } from '../../core/services/data-store.service';

@Component({
  selector: 'app-entity-page',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './entity-page.component.html',
  styleUrl: './entity-page.component.css'
})
export class EntityPageComponent {
  protected config: CrudEntityConfig | null = null;
  protected records: CrudRecord[] = [];
  protected draft: Record<string, string> = {};
  protected isModalOpen = false;
  protected isEditMode = false;
  protected formError = '';
  protected openDetailedFieldKey: string | null = null;

  private selectedRecordId: string | null = null;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dataStoreService: DataStoreService
  ) {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const entity = params.get('entity');

        if (!isCrudEntityKey(entity)) {
          void this.router.navigate(['/dashboard']);
          return;
        }

        this.config = CRUD_ENTITY_CONFIGS[entity];
        this.refreshRecords(entity);
      });
  }

  get fields(): FieldDefinition[] {
    return this.config?.formFields ?? [];
  }

  openCreateModal(): void {
    if (!this.config) {
      return;
    }

    this.isEditMode = false;
    this.selectedRecordId = null;
    this.formError = '';
    this.openDetailedFieldKey = null;
    this.draft = this.buildEmptyDraft(this.config);
    this.isModalOpen = true;
  }

  openEditModal(record: CrudRecord): void {
    if (!this.config) {
      return;
    }

    this.isEditMode = true;
    this.selectedRecordId = record.id;
    this.formError = '';
    this.openDetailedFieldKey = null;
    this.draft = this.fields.reduce<Record<string, string>>((collection, field) => {
      collection[field.key] = record[field.key] ?? '';
      return collection;
    }, {});
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.formError = '';
    this.openDetailedFieldKey = null;
  }

  saveRecord(): void {
    if (!this.config) {
      return;
    }

    const missingField = this.fields.find(
      (field) => field.required && !this.draft[field.key]?.trim()
    );

    if (missingField) {
      this.formError = `${missingField.label} is required.`;
      return;
    }

    const cleanedDraft = this.fields.reduce<Record<string, string>>((collection, field) => {
      collection[field.key] = (this.draft[field.key] ?? '').trim();
      return collection;
    }, {});

    if (this.isEditMode && this.selectedRecordId) {
      this.dataStoreService.updateRecord(this.config.key, this.selectedRecordId, cleanedDraft);
    } else {
      this.dataStoreService.addRecord(this.config.key, cleanedDraft);
    }

    this.refreshRecords(this.config.key);
    this.closeModal();
  }

  deleteRecord(record: CrudRecord): void {
    if (!this.config) {
      return;
    }

    const shouldDelete = window.confirm(`Delete ${this.config.singular.toLowerCase()} ${record.id}?`);

    if (!shouldDelete) {
      return;
    }

    this.dataStoreService.deleteRecord(this.config.key, record.id);
    this.refreshRecords(this.config.key);
  }

  updateDraft(fieldKey: string, value: string): void {
    this.draft = {
      ...this.draft,
      [fieldKey]: value
    };
  }

  getCellValue(record: CrudRecord, fieldKey: string): string {
    return record[fieldKey] ?? '-';
  }

  hasDetailedOptions(field: FieldDefinition): boolean {
    return (field.options ?? []).some((option) => Boolean(option.description));
  }

  isSelectedOption(field: FieldDefinition, option: FieldOption): boolean {
    return this.draft[field.key] === option.value;
  }

  getSelectedOption(field: FieldDefinition): FieldOption | null {
    return field.options?.find((option) => option.value === this.draft[field.key]) ?? null;
  }

  isDetailedDropdownOpen(fieldKey: string): boolean {
    return this.openDetailedFieldKey === fieldKey;
  }

  toggleDetailedDropdown(fieldKey: string): void {
    this.openDetailedFieldKey = this.openDetailedFieldKey === fieldKey ? null : fieldKey;
  }

  selectDetailedOption(field: FieldDefinition, option: FieldOption): void {
    this.updateDraft(field.key, option.value);
    this.openDetailedFieldKey = null;
  }

  getTrackByRecord(_: number, record: CrudRecord): string {
    return record.id;
  }

  private refreshRecords(entity: CrudEntityKey): void {
    this.records = this.dataStoreService.getRecords(entity);
  }

  private buildEmptyDraft(config: CrudEntityConfig): Record<string, string> {
    return config.formFields.reduce<Record<string, string>>((collection, field) => {
      collection[field.key] = field.options?.[0]?.value ?? '';
      return collection;
    }, {});
  }
}